import React, { useCallback, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

import { Container, Dialog, DialogContent, Tab, Tabs } from '@mui/material';

import { property as propertyGlobals } from '@luxuryescapes/lib-global';

import SuppliersPropertiesPage from '~/components/Bedbank/Properties/Home/SuppliersProperties/PageContainer';
import MappingIssuesPage from '~/components/Bedbank/Properties/MappingIssues/MappingIssuesPage';
import RoomMappingCandidatesPage from '~/components/Bedbank/Properties/MappingIssues/RoomMappingCandidatesPage';
import RoomMappingIssuesPage from '~/components/Bedbank/Properties/MappingIssues/RoomMappingIssuesPage';
import PotentialPropertyMappingsPage from '~/components/Bedbank/Properties/PotentialPropertyMappings/PotentialPropertyMappingsPage';
import PageHeader from '~/components/Common/Elements/PageHeader';
import Image from '~/components/Common/Image';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';

import { isAdmin } from '~/utils/adminPermission';

import CurationDashboardPage from '../Curation/CurationDashboardPage';

import DynamicConfigurationsPage from './DynamicConfiguration/DynamicConfigurationsPage';
import PageContainer from './PageContainer';

const HOTEL_TAB = propertyGlobals.HOTEL_TYPE.toLowerCase();
const UNIQUE_STAYS_TAB = propertyGlobals.UNIQUE_STAYS_TYPE.toLowerCase();

const DYNAMIC_CONFIGURATIONS_TYPE = 'dynamic_configurations';
const MAPPING_ISSUES_TYPE = 'mapping_issues';
const SUPPLIERS_PROPERTIES_TYPE = 'suppliers_properties';
const ROOM_MAPPING_ISSUES_TYPE = 'room_mapping_issues';
const ROOM_MAPPING_CANDIDATES_TYPE = 'room_mapping_candidates';
const POTENTIAL_PROPERTY_MAPPINGS_TYPE = 'potential_property_mappings';
const CURATION_DASHBOARD_TYPE = 'curation_dashboard';

function BedbankPropertiesPage() {
  const { push, replace } = useAppNavigation();
  const location = useLocation();
  const { user } = useCurrentUser();
  const userIsAdmin = isAdmin(user);

  // The URL hash is the single source of truth for the selected tab — this lets
  // PageContainer switch tabs (e.g. a search found on the other property type)
  // with a plain navigation, no callback plumbing needed.
  const selectedTab = location.hash.replace('#', '') || HOTEL_TAB;
  const [selectedImageId, setSelectedImageId] = useState(null);

  const handleTabChange = useCallback(
    (_, value) => {
      replace({
        pathname: location.pathname,
        search: value === HOTEL_TAB || value === UNIQUE_STAYS_TAB ? location.search : '',
        hash: value,
      });
    },
    [replace, location.pathname, location.search],
  );

  return (
    <>
      <Container maxWidth="xl">
        <Helmet>
          <title>Bedbank - Properties</title>
        </Helmet>
        <PageHeader title="Bedbank - Properties" />
        <Tabs sx={{ mb: 2 }} value={selectedTab} onChange={handleTabChange}>
          <Tab
            key={propertyGlobals.HOTEL_TYPE.toLowerCase()}
            value={propertyGlobals.HOTEL_TYPE.toLowerCase()}
            label="Hotels & Resorts"
          />
          <Tab
            key={propertyGlobals.UNIQUE_STAYS_TYPE.toLowerCase()}
            value={propertyGlobals.UNIQUE_STAYS_TYPE.toLowerCase()}
            label="Unique Stays"
          />
          <Tab key={SUPPLIERS_PROPERTIES_TYPE} value={SUPPLIERS_PROPERTIES_TYPE} label="Suppliers Properties" />
          <Tab key={MAPPING_ISSUES_TYPE} value={MAPPING_ISSUES_TYPE} label="Mapping Issues" />
          <Tab key={ROOM_MAPPING_ISSUES_TYPE} value={ROOM_MAPPING_ISSUES_TYPE} label="Room Mapping Issues" />
          <Tab
            key={ROOM_MAPPING_CANDIDATES_TYPE}
            value={ROOM_MAPPING_CANDIDATES_TYPE}
            label="Room Mapping Candidates"
          />
          <Tab
            key={POTENTIAL_PROPERTY_MAPPINGS_TYPE}
            value={POTENTIAL_PROPERTY_MAPPINGS_TYPE}
            label="Potential Property Mappings"
          />
          <Tab key={CURATION_DASHBOARD_TYPE} value={CURATION_DASHBOARD_TYPE} label="Curation Dashboard" />
          {userIsAdmin && (
            <Tab key={DYNAMIC_CONFIGURATIONS_TYPE} value={DYNAMIC_CONFIGURATIONS_TYPE} label="Dynamic Configurations" />
          )}
        </Tabs>

        {selectedTab !== DYNAMIC_CONFIGURATIONS_TYPE &&
          selectedTab !== SUPPLIERS_PROPERTIES_TYPE &&
          selectedTab !== MAPPING_ISSUES_TYPE &&
          selectedTab !== ROOM_MAPPING_ISSUES_TYPE &&
          selectedTab !== ROOM_MAPPING_CANDIDATES_TYPE &&
          selectedTab !== POTENTIAL_PROPERTY_MAPPINGS_TYPE &&
          selectedTab !== CURATION_DASHBOARD_TYPE && (
            <>
              <PageContainer
                onOpenModalImage={setSelectedImageId}
                location={location}
                history={{ push, replace }}
                propertyType={selectedTab.toUpperCase()}
              />
              <Dialog
                sx={{
                  '& .MuiDialog-container': {
                    '& .MuiPaper-root': {
                      maxWidth: '100%',
                    },
                  },
                }}
                open={!!selectedImageId}
                onClose={() => setSelectedImageId(null)}
              >
                <DialogContent sx={{ display: 'flex', height: '90vh' }}>
                  {selectedImageId && (
                    <Image className="bedbank-img-responsive" publicId={selectedImageId} options={{ width: 1024 }} />
                  )}
                </DialogContent>
              </Dialog>
            </>
          )}

        {selectedTab === DYNAMIC_CONFIGURATIONS_TYPE && userIsAdmin && <DynamicConfigurationsPage />}
        {selectedTab === MAPPING_ISSUES_TYPE && <MappingIssuesPage />}
        {selectedTab === SUPPLIERS_PROPERTIES_TYPE && (
          <SuppliersPropertiesPage location={location} history={{ push }} />
        )}
        {selectedTab === ROOM_MAPPING_ISSUES_TYPE && <RoomMappingIssuesPage />}
        {selectedTab === ROOM_MAPPING_CANDIDATES_TYPE && <RoomMappingCandidatesPage />}
        {selectedTab === POTENTIAL_PROPERTY_MAPPINGS_TYPE && <PotentialPropertyMappingsPage />}
        {selectedTab === CURATION_DASHBOARD_TYPE && <CurationDashboardPage />}
      </Container>
    </>
  );
}

export default BedbankPropertiesPage;
